/*
 * <announcement-bar> — the rotating announcement strip.
 *
 * HTML and CSS already lay the bar out and, on desktop, show every item side by
 * side. This element only adds the two things markup cannot do:
 *
 *   1. measure the tallest item and pin the wrapper to it, so a message that
 *      wraps on a narrow screen is never clipped by the absolute positioning;
 *   2. drive the rotation, restarting the animation each cycle.
 *
 * With JS off the wrapper keeps its CSS min-height and the first item is
 * readable, because .animate is what hides the rest.
 */
if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.querySelector('.announcement-bar--wrapper');
      this.items = this.wrapper ? Array.from(this.wrapper.querySelectorAll('.announcement-bar--item')) : [];
      if (!this.wrapper || this.items.length === 0) return;

      this.cycle = Number(this.dataset.cycle || 4);
      this.total = this.cycle * this.items.length;

      this.onResize = this.fit.bind(this);
      this.fit();
      window.addEventListener('resize', this.onResize);

      if (this.items.length > 1) this.rotate();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      clearTimeout(this.cycleTimer);
      clearTimeout(this.restartTimer);
    }

    /* Absolute positioning removes the items from flow, so the wrapper has no
     * natural height on mobile. Clone each item into a hidden probe of the same
     * width and take the tallest result.
     *
     * The probe is mounted INSIDE this element, not on <body>: section-scoped CSS
     * has to reach the clones or they measure against the wrong type scale.
     *
     * The result only ever RAISES the wrapper past its CSS min-height, so the bar
     * keeps live's fixed height until a message genuinely needs to wrap. */
    fit() {
      const styles = getComputedStyle(this.wrapper);
      const padding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      const floor = parseFloat(styles.minHeight) || 0;
      const width = this.wrapper.offsetWidth || document.documentElement.clientWidth;

      const probe = document.createElement('div');
      probe.setAttribute('aria-hidden', 'true');
      probe.style.cssText =
        'position:absolute;visibility:hidden;top:-9999px;left:0;pointer-events:none;z-index:-1;width:' + width + 'px';
      this.appendChild(probe);

      let tallest = 0;
      this.items.forEach((item) => {
        const clone = item.cloneNode(true);
        probe.appendChild(clone);
        clone.style.setProperty('position', 'static', 'important');
        clone.style.setProperty('height', 'auto', 'important');
        clone.style.setProperty('left', 'auto', 'important');
        clone.style.setProperty('opacity', '1', 'important');
        clone.style.setProperty('width', '100%', 'important');
        clone.style.setProperty('min-width', '0', 'important');
        tallest = Math.max(tallest, clone.offsetHeight);
        probe.removeChild(clone);
      });

      this.removeChild(probe);
      this.wrapper.style.height = Math.max(tallest + padding, floor) + 'px';
    }

    rotate() {
      this.items.forEach((item, index) => {
        item.style.setProperty('--announcement-cycle', this.cycle + 's');
        item.style.setProperty('--announcement-delay', index * this.cycle + 's');
        item.classList.add('animate');
      });

      this.cycleTimer = setTimeout(() => {
        this.items.forEach((item) => item.classList.remove('animate'));
        this.restartTimer = setTimeout(() => this.rotate(), 100);
      }, this.total * 1000);
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);
}

/*
 * <announcement-countdown> — a single "00d 00h 00m 00s" readout.
 */
if (!customElements.get('announcement-countdown')) {
  class AnnouncementCountdown extends HTMLElement {
    connectedCallback() {
      this.end = new Date(this.dataset.end);
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const remaining = this.end - new Date();
      if (isNaN(this.end.getTime()) || remaining <= 0) {
        this.textContent = '00d 00h 00m 00s';
        clearInterval(this.timer);
        return;
      }
      const pad = (n) => String(n).padStart(2, '0');
      const days = Math.floor(remaining / 86400000);
      const hours = Math.floor((remaining % 86400000) / 3600000);
      const minutes = Math.floor((remaining % 3600000) / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      this.textContent = pad(days) + 'd ' + pad(hours) + 'h ' + pad(minutes) + 'm ' + pad(seconds) + 's';
    }
  }
  customElements.define('announcement-countdown', AnnouncementCountdown);
}

/*
 * <rating-bar> carries no behaviour. It is markup only — the wrapper the
 * announcement-bar stylesheet lays out (.rating-bar__inner-wrapper).
 *
 * It used to fetch api.reviews.io itself and write [data-rating] /
 * [data-count]. `snippets/reviewsio-merchant-stats.liquid` (P13, registered in
 * docs/script-register.md and rendered from theme.liquid) fetches the SAME
 * endpoint on every page and writes [data-reviewsio-rating] /
 * [data-reviewsio-count]. Keeping both meant two requests per page against
 * live's one, and — because only this one applied the stored static-count
 * offset — two different numbers racing for the same span, so the rendered
 * count depended on which response landed last.
 *
 * The registered snippet won: it is the one the script register and the P15
 * consent trace account for, and its no-offset count is what live renders.
 * snippets/rating-bar.liquid now emits the contract's attributes.
 */

